import { useState } from 'react';
import Image from 'next/image';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';

import ChevronLeft from '@/icons/light/chevron-left.svg';
import ChevronRight from '@/icons/light/chevron-right.svg';

import type { Options, Splide as TypeSplide } from '@splidejs/splide';
import type { FunctionComponent, ReactNode } from 'react';
import type { TypeGalleryMobileProps } from '.';

import '@splidejs/react-splide/css/core';

const ARROWS =
  'rounded-full bg-neutrals-0 hover:bg-primary-100 text-neutrals-900 p-2 disabled:opacity-60 disabled:hover:bg-neutrals-0';

export const GalleryMobile: FunctionComponent<TypeGalleryMobileProps> = ({
  className = '',
  classNameSlide = '',
  breakpoints,
  perPage,
  height = '300px',
  type,
  gap = 24,
  images,
  onClick,
}: TypeGalleryMobileProps) => {
  const DEFUALT_PADDING = '15px';
  const adjustPadding = (
    splide: TypeSplide,
    track: HTMLElement,
    rootParent: HTMLElement | null,
    start: number,
    last: number,
    padding: Options['padding'],
  ) => {
    if (padding) {
      if (start === splide.index) {
        track.style.paddingLeft = DEFUALT_PADDING;
      } else {
        track.style.paddingLeft = `${padding}px`;
      }

      if (last === splide.index) {
        track.style.paddingRight = DEFUALT_PADDING;
      } else {
        track.style.paddingRight = `${padding}px`;
      }
    } else {
      track.style.paddingLeft = '0';
      track.style.paddingRight = '0';
    }
  };
  const adjustAllPaddings = (splide: TypeSplide) => {
    const last = splide.Components.Controller.getEnd();
    const start = 0;
    const padding = splide.options.padding;
    const track = splide.Components.Elements.track;
    const root = splide.Components.Elements.root;
    const rootParent = root.parentElement;
    const breakpoints = splide.options.breakpoints;

    if (breakpoints) {
      Object.entries(breakpoints).forEach(([key, value], index) => {
        if (
          window.screen.width <= parseInt(key) &&
          ((index > 0 &&
            window.screen.width >
              parseInt(Object.keys(breakpoints)[index - 1])) ||
            index === 0)
        ) {
          adjustPadding(splide, track, rootParent, start, last, value.padding);
        }
      });
    }

    adjustPadding(splide, track, rootParent, start, last, padding);
  };

  const options: Options = {
    ...(type && { type }),
    perPage: perPage,
    ...(breakpoints && { breakpoints }),
    ...(height && { height }),
    ...(gap && { gap }),
    perMove: 1,
    pagination: false,
    arrows: perPage !== images.length,
    lazyLoad: true,
    classes: {
      prev: 'rotate-180',
    },
  };

  /**
   * Render slides.
   *
   * @return Slide nodes.
   */
  const renderSlides = (): ReactNode => (
    <SplideTrack className="!md:p-5">
      {images.map((image, index) => (
        <SplideSlide
          key={index}
          className={`relative w-full ${classNameSlide}`}
          style={{ height }}
        >
          <Image
            alt={image.alt ?? `Imagen del carrusel ${index + 1}`}
            src={image.src}
            draggable={false}
            fill
            className="object-cover"
          />
        </SplideSlide>
      ))}
    </SplideTrack>
  );

  //TODO revisar contador

  const [counter, setCounter] = useState(1);

  return (
    <Splide
      options={options}
      className={`relative -mx-4 flex flex-col lg:mx-0 ${className}`}
      hasTrack={false}
      {...(onClick && { onClick: onClick })}
      onMove={(splide: any, index: any) => {
        setCounter(index + 1);
        adjustAllPaddings(splide);
      }}
      onMounted={(splide: any) => {
        setCounter(1);
        adjustAllPaddings(splide);
      }}
    >
      {renderSlides()}

      <div className="splide__arrows">
        <button
          className={`splide__arrows splide__arrow--prev ${ARROWS} absolute top-1/2 left-4 -translate-y-1/2`}
          type="button"
          role="tab"
        >
          <ChevronLeft width={24} height={24} />
        </button>

        <button
          className={`splide__arrows splide__arrow--next ${ARROWS} absolute top-1/2 right-4 -translate-y-1/2`}
          type="button"
          role="tab"
        >
          <ChevronRight width={24} height={24} />
        </button>
      </div>

      <div className="text-body text-body-xs bg-neutrals-900 bg-opacity-40 text-neutrals-0 absolute bottom-2 left-2 min-h-8 min-w-10 rounded-full p-2 font-bold">
        {counter}/{images.length}
      </div>
    </Splide>
  );
};
